/**
 * "Move to live now" — the whole flow behind one context.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Rows sit four levels deep (view → layer section → rule group → row), so the
 * action travels by context rather than prop drilling. Clicking PLANS first (a
 * read): free capacity promotes outright, otherwise the picker opens. A rotation
 * rule's verdict PRESELECTS a victim but never locks it — replacing something
 * else, or raising the cap, stays available. Only confirm writes.
 */

import { FC, ReactNode, createContext, useContext, useState } from 'react';
import type {
  CapacityRule,
  PromotePlanResult,
  QueueElement,
} from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { raiseCapFor, sharedGroupCount } from './cap-raise';
import { ChooseBody, type PromoteChoice } from './PromoteDialogBody';
import { usePromoteActions } from './use-promote-actions';

interface PromoteFlowValue {
  request: (element: QueueElement) => void;
  pendingBankId: string | null;
  /** Non-null when the action cannot run at all (e.g. kill switch on). */
  disabledReason: string | null;
}

const Ctx = createContext<PromoteFlowValue | null>(null);

/** Rows use this; outside the provider it returns null and they hide the button. */
export const usePromoteFlow = (): PromoteFlowValue | null => useContext(Ctx);

export interface PromoteFlowProviderProps {
  campaignId: string;
  provider: QueueProvider;
  killSwitch: boolean;
  poolIds: string[];
  capacityRules: CapacityRule[];
  onCapacityRulesChange?: (next: CapacityRule[]) => void;
  children: ReactNode;
}

const KILL_SWITCH_REASON =
  'Tracker pushes are paused (kill switch). Turn pushes back on to move an element live.';

export const PromoteFlowProvider: FC<PromoteFlowProviderProps> = ({
  campaignId,
  provider,
  killSwitch,
  poolIds,
  capacityRules,
  onCapacityRulesChange,
  children,
}) => {
  const toast = useToast();
  const [open, setOpen] = useState<PromotePlanResult | null>(null);
  const [choice, setChoice] = useState<PromoteChoice | null>(null);
  const actions = usePromoteActions({
    campaignId,
    provider,
    capacityRules,
    onCapacityRulesChange,
    onDone: () => setOpen(null),
  });

  const request = (element: QueueElement) => {
    setChoice(null);
    actions.plan.mutate(
      { layer: element.layer, bankId: element.bankId },
      {
        onSuccess: (p) => {
          if (p.mode === 'blocked') {
            toast.error('Cannot move this live', p.blockReason);
            return;
          }
          if (p.mode === 'free') {
            void actions.live(p);
            return;
          }
          // A rule's verdict preselects, so confirming straight away does what
          // it always did — but every other option stays on the table.
          if (p.victim) {
            setChoice({ kind: 'replace', victimBankId: p.victim.bankId });
          }
          setOpen(p);
        },
        onError: (e) =>
          toast.error('Could not check capacity', getApiErrorMessage(e)),
      },
    );
  };

  const confirm = () => {
    if (!open || !choice) return;
    if (choice.kind === 'raise-cap') return void actions.raiseCapAndLive(open);
    return void actions.live(open, choice.victimBankId);
  };
  const scope = open?.pathScope ?? 'default';
  const shared = open ? sharedGroupCount(scope, poolIds) : 0;
  const canRaiseCap = Boolean(
    open && raiseCapFor(capacityRules, scope, (open.cap ?? 0) + 1),
  );
  const inFlight = actions.plan.isPending
    ? actions.plan.variables
    : actions.promote.isPending
      ? actions.promote.variables
      : null;

  return (
    <Ctx.Provider
      value={{
        request,
        pendingBankId: inFlight?.bankId ?? null,
        disabledReason: killSwitch ? KILL_SWITCH_REASON : null,
      }}
    >
      {children}
      <ConfirmDialog
        open={Boolean(open)}
        title="Move to live now"
        widthClass="max-w-lg"
        confirmLabel={
          choice?.kind === 'raise-cap' ? 'Raise the cap & go live' : 'Replace'
        }
        confirmDisabled={!choice}
        busy={actions.busy}
        description={
          open ? (
            <ChooseBody
              plan={open}
              choice={choice}
              onChoice={setChoice}
              sharedGroups={shared}
              canRaiseCap={canRaiseCap}
            />
          ) : null
        }
        onConfirm={confirm}
        onCancel={() => setOpen(null)}
      />
    </Ctx.Provider>
  );
};
